import { distancePointSegment } from '../../../geo/polygon';
import type { Vec2 } from '../../../geo/types';

/**
 * The way through a gate. A gate's building stands on its street, so the street's points stop at
 * its walls on either side; the passage under it joins them. Without it everything outside the
 * walls, the Lastage and its yard among it, is a piece of the net on its own.
 */
export const GATE_WAY = {
  /** The two points joined lie within this far of the gate. */
  reachM: 30,
  /** The line between them passes this near the gate's own point: through it, not round it. */
  offLineM: 3,
} as const;

/** Joins, at each gate, the two nearest points of pieces of the net that the gate lies between. */
export function joinGates(
  draft: { readonly nodes: number[]; readonly edges: number[] },
  gates: readonly { readonly pos: Vec2 }[]
): void {
  const count = draft.nodes.length / 3;
  const parent = Int32Array.from({ length: count }, (_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]!]!;
    return i;
  };
  for (let e = 0; e < draft.edges.length; e += 2)
    parent[find(draft.edges[e]!)] = find(draft.edges[e + 1]!);
  const at = (i: number): Vec2 => [draft.nodes[i * 3]!, draft.nodes[i * 3 + 1]!];
  for (const gate of gates) {
    const near: number[] = [];
    for (let i = 0; i < count; i++) {
      const p = at(i);
      if (Math.hypot(p[0] - gate.pos[0], p[1] - gate.pos[1]) <= GATE_WAY.reachM) near.push(i);
    }
    let pair: readonly [number, number] | undefined,
      shortest = Infinity;
    for (const i of near)
      for (const j of near) {
        if (j <= i || find(i) === find(j)) continue;
        const [a, b] = [at(i), at(j)];
        const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (length >= shortest || distancePointSegment(gate.pos, { a, b }) > GATE_WAY.offLineM)
          continue;
        [pair, shortest] = [[i, j], length];
      }
    if (!pair) continue;
    draft.edges.push(pair[0], pair[1]);
    parent[find(pair[0])] = find(pair[1]);
  }
}
